import { Upload } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib';
import type { DropzoneSize } from './types';

/** Классы частей области загрузки по её размеру */
const SIZE_CLASSES: Record<
  DropzoneSize,
  { root: string; content: string; icon: string; glyph: string; texts: string; title: string; hint: string }
> = {
  default: {
    root: 'min-h-50 p-8',
    content: 'space-y-4',
    icon: 'mx-auto size-16',
    glyph: 'size-8',
    texts: 'space-y-1',
    title: 'text-lg',
    hint: 'text-sm',
  },
  compact: {
    root: 'p-3',
    content: 'flex items-center gap-3 text-left',
    icon: 'size-9 shrink-0',
    glyph: 'size-4',
    texts: 'min-w-0 space-y-0.5',
    title: 'text-sm',
    hint: 'text-xs',
  },
};

type DropzonePresenterProps = {
  isDragOver: boolean;
  isDisabled: boolean;
  acceptedFileTypes: string[];
  maxFileSize: number;
  maxFiles: number;
  size: DropzoneSize;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
};

/** Разметка области загрузки файлов */
export const DropzonePresenter: FC<DropzonePresenterProps> = ({
  isDragOver,
  isDisabled,
  acceptedFileTypes,
  maxFileSize,
  maxFiles,
  size,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileInput,
  className,
}) => {
  const { t } = useTranslation(['records']);
  const sizeClasses = SIZE_CLASSES[size];

  return (
    <div
      className={cn(
        'relative flex items-center justify-center rounded-xl border-2 border-dashed text-center transition-all duration-200',
        sizeClasses.root,
        isDisabled
          ? 'border-muted bg-muted/30 cursor-not-allowed opacity-60'
          : isDragOver
            ? 'border-primary bg-primary/5 shadow-md'
            : 'border-border hover:border-primary/50 hover:bg-muted/50',
        className,
      )}
      onDragOver={isDisabled ? undefined : onDragOver}
      onDragLeave={isDisabled ? undefined : onDragLeave}
      onDrop={isDisabled ? undefined : onDrop}
    >
      <input
        type="file"
        multiple
        accept={acceptedFileTypes.join(',')}
        onChange={onFileInput}
        disabled={isDisabled}
        className={cn('absolute inset-0 opacity-0', isDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
        aria-label={t('records:fileUpload.selectFiles')}
        aria-disabled={isDisabled}
      />

      <div className={sizeClasses.content}>
        <div
          className={cn(
            'flex items-center justify-center rounded-full transition-all',
            sizeClasses.icon,
            isDisabled
              ? 'bg-muted/50 text-muted-foreground'
              : isDragOver
                ? 'scale-105 bg-primary/10 text-primary'
                : 'bg-muted text-muted-foreground',
          )}
        >
          <Upload className={sizeClasses.glyph} />
        </div>

        <div className={sizeClasses.texts}>
          <p className={cn('font-medium', sizeClasses.title, isDisabled ? 'text-muted-foreground' : 'text-foreground')}>
            {isDisabled ? t('records:fileUpload.maxFilesReached', { maxFiles }) : t('records:fileUpload.dragAndDrop')}
          </p>
          <p className={cn('text-muted-foreground', sizeClasses.hint)}>
            {t('records:fileUpload.fileInfo', {
              types: acceptedFileTypes.join(', '),
              maxSize: maxFileSize,
              filesCount: t('records:fileUpload.filesCount', { count: maxFiles }),
            })}
          </p>
        </div>
      </div>
    </div>
  );
};
